/**
 * New dashboards default to Reporting with tenant filtering. Existing drafts
 * retain explicit choices, and older published dashboards retain their Live
 * behavior. Changing purpose does not reset viewer controls.
 */
import type { WidgetPurpose } from "./catalogPresentation";
import type { DashboardConfig, TenantScope } from "./dashboardContext";
import { defaultRefresh, defaultTimeRange } from "./timeRange";
import { getRefreshSchedule } from "./timePresets";

export interface ContextChoices {
  purpose?: WidgetPurpose;
  tenants?: TenantScope;
}

export function readContextChoices(
  config: DashboardConfig,
  { neverPublished }: { neverPublished: boolean },
): ContextChoices {
  const purpose =
    config.refreshMode === "report"
      ? "reporting"
      : config.refreshMode === "live"
        ? "live"
        : neverPublished
          ? undefined
          : "live";
  const tenants = config.scope?.tenants ?? (neverPublished ? undefined : "selected");
  return { purpose, tenants };
}

export function writePurpose(config: DashboardConfig, purpose: WidgetPurpose): DashboardConfig {
  const previous = config.refreshMode === "report" ? "reporting" : "live";
  const currentRange = config.scope?.timeRange;
  const previousRange = defaultTimeRange(previous);
  const previousRefresh = defaultRefresh(previous);
  const useDefaultRange =
    !currentRange ||
    (currentRange.mode === "relative" &&
      previousRange.mode === "relative" &&
      currentRange.amount === previousRange.amount &&
      currentRange.unit === previousRange.unit &&
      currentRange.endAt === previousRange.endAt);
  const useDefaultRefresh =
    !config.refresh ||
    (config.refresh.intervalSeconds === previousRefresh.intervalSeconds &&
      config.refresh.onFocus === previousRefresh.onFocus &&
      getRefreshSchedule(config.refresh) === getRefreshSchedule(previousRefresh));
  return {
    ...config,
    refreshMode: purpose === "reporting" ? "report" : "live",
    scope: {
      ...config.scope,
      ...(useDefaultRange ? { timeRange: defaultTimeRange(purpose) } : {}),
    },
    ...(useDefaultRefresh ? { refresh: defaultRefresh(purpose) } : {}),
  };
}

/** Stored explicitly, so a chosen "selected" is told apart from no choice yet. */
export function writeTenantScope(config: DashboardConfig, tenants: TenantScope): DashboardConfig {
  return { ...config, scope: { ...config.scope, tenants } };
}

/** New dashboards start in Reporting with viewer-controlled tenant filtering. */
export function blankDashboardConfig(): DashboardConfig {
  return {
    version: 1,
    refreshMode: "report",
    filters: ["tenants"],
    scope: { tenants: "selected", timeRange: defaultTimeRange("reporting") },
    refresh: defaultRefresh("reporting"),
    composition: { blocks: [{ type: "widgets" }] },
  };
}
